import type { ThemeOptions } from "@mui/material";

import type { DesignerEmotionTheme } from "./DesignerTheme.ts";
import { colors } from "./Palette.ts";

const { neutral, uiGrey } = colors;

const fontFamily = "'Inter', sans-serif";

export const createMaterialThemeOptions = (theme: DesignerEmotionTheme): ThemeOptions =>
  ({
    shape: {
      borderRadius: theme.borderRadius ?? 3,
    },
    palette: {
      common: {
        black: neutral.black,
        white: neutral.white,
      },
      primary: {
        main: theme.primary,
        light: theme.primaryAccent,
        dark: theme.primaryStrong,
        contrastText: theme.onPrimary,
      },
      secondary: {
        main: theme.secondaryContainer,
        light: theme.secondarySelection,
        dark: theme.secondaryStrong,
        contrastText: theme.onSecondaryContainer,
      },
      error: {
        main: theme.error,
        contrastText: theme.onError,
      },
      warning: {
        main: theme.warning,
        contrastText: theme.onWarning,
      },
      info: {
        main: theme.primaryContainer,
        contrastText: theme.onPrimaryContainer,
      },
      success: {
        main: theme.tertiary,
        contrastText: theme.onPrimary,
      },
      grey: {
        "50": uiGrey["000"],
        "100": uiGrey["050"],
        "200": uiGrey["100"],
        "300": uiGrey["150"],
        "400": uiGrey["200"],
        "500": uiGrey["250"],
        "600": uiGrey["350"],
        "700": uiGrey["400"],
        "800": uiGrey["500"],
        "900": uiGrey["700"],
        A100: "#d5d5d5",
        A200: "#aaaaaa",
        A400: "#303030",
        A700: "#616161",
      },
      contrastThreshold: 3,
      tonalOffset: 0.2,
      divider: theme.outlineSoft,
      action: {
        active: theme.generalTextIcon,
        hover: "rgba(0, 0, 0, 0.04)",
        hoverOpacity: 0.04,
        selected: "rgba(0, 0, 0, 0.08)",
        selectedOpacity: 0.08,
        disabled: "rgba(0, 0, 0, 0.26)",
        disabledBackground: "rgba(0, 0, 0, 0.12)",
        disabledOpacity: 0.38,
        focus: "rgba(0, 0, 0, 0.12)",
        focusOpacity: 0.12,
        activatedOpacity: 0.12,
      },
    },
    typography: {
      fontFamily,
      fontWeightLight: 200,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontWeightBold: 800,
      allVariants: {
        fontSize: theme.fontSize,
      },
    },
    components: {
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: "inherit",
            color: "inherit",
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            "&::before": {
              borderBottom: `1px solid ${theme.outlineSoft}`,
            },
            "&:hover:not(.Mui-disabled, .Mui-error):before": {
              borderBottom: `1px solid ${theme.outline}`,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: "inherit",
            lineHeight: "inherit",
            minHeight: "28px",
            "&:hover": {
              backgroundColor: theme.surface,
            },
            "&::before": {
              borderBottom: `1px solid ${theme.outlineSoft}`,
            },
            "&:hover:not(.Mui-disabled, .Mui-error):before": {
              borderBottom: `1px solid ${theme.outline}`,
            },
          },
          input: {
            paddingBottom: "3px",
            "&:focus": {
              background: "inherit",
            },
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            pointerEvents: "none",
            zIndex: 1,
          },
          shrink: {
            fontSize: "1em",
            transform: "translate(0, 0)",
            pointerEvents: "auto",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: "small",
          variant: "standard",
          margin: "none",
          fullWidth: true,
        },
        styleOverrides: {
          root: {
            "--TextField-brandBorderColor": theme.outlineSoft,
            "--TextField-brandBorderHoverColor": theme.outline,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: theme.fontSizeLarge,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            margin: 0,
            fontSize: "0.875em",
          },
          contained: {
            marginLeft: 0,
            marginRight: 0,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
          },
          sizeSmall: {
            fontSize: theme.fontSizeSmall,
          },
          sizeMedium: {
            fontSize: theme.fontSize,
          },
          sizeLarge: {
            fontSize: theme.fontSizeLarge,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: "inherit",
            borderRadius: theme.borderRadius ?? "4",
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
          enterDelay: 500,
          enterTouchDelay: 0,
          disableInteractive: true,
        },
        styleOverrides: {
          tooltip: {
            fontSize: theme.fontSize,
            background: theme.neutralContainer,
            color: theme.onNeutral,
          },
          arrow: {
            color: theme.neutralContainer,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              color: theme.primaryStrong,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: theme.primaryStrong,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            background: theme.primaryAccent,
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          size: "small",
          fullWidth: true,
        },
        styleOverrides: {
          root: {
            ".MuiInput-root": {
              paddingBottom: 0,
              minHeight: "28px",
            },
          },
          endAdornment: {
            right: "10px",
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: "small",
          variant: "standard",
        },
        styleOverrides: {
          root: {
            paddingBottom: 0,
            height: "28px",
          },
          select: {
            maxWidth: 320,
            paddingBottom: "3px",
          },
          icon: {
            color: "inherit",
            fontSize: "12px",
            top: "calc(50% - 0.7em)",
            right: "10px",
          },
        },
      },
      MuiChip: {
        defaultProps: {
          color: "secondary",
        },
      },
      MuiPopover: {
        defaultProps: {
          closeAfterTransition: true,
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            background: theme.surfaceBack_2,
            borderRadius: 5,
            height: 6,
          },
          bar1: {
            background: `linear-gradient(to right, ${theme.primary}, ${theme.secondary})`,
            borderRadius: 5,
          },
          bar2: {
            background: `linear-gradient(to right, ${theme.primary}, ${theme.secondary})`,
            borderRadius: 5,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: theme.surface,
          },
        },
      },
      MuiMobileStepper: {
        styleOverrides: {
          root: {
            backgroundColor: theme.surface,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: theme.surfaceVariant,
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          fullWidth: true,
        },
      },
    },
  }) as const;
